<!--
  @fileoverview Facebook Component - Facebook Messenger Integration (Vue 3)

  Integrates Facebook Messenger Customer Chat plugin into the webchat widget.
  This component loads the Facebook SDK and renders the fb-customerchat element
  for embedded Facebook Messenger functionality.

  @component Facebook
  @displayName Facebook Messenger Integration

  Features:
  - Loads Facebook SDK asynchronously
  - Configures Customer Chat plugin with page_id and app_id
  - Provides show/hide methods for programmatic control
  - RTL layout support

  @example
  <Facebook
    :facebook="{
      page_id: '123456789',
      messenger_app_id: 'abc123',
      theme_color: '#0084FF'
    }"
  />

  @props
  - facebook {Object} - Facebook configuration object
    - page_id {string} - Facebook Page ID
    - messenger_app_id {string} - Facebook Messenger App ID
    - data-href {string} - Reference URL (defaults to keyreply.com)
    - ref {string} - Reference identifier (defaults to hostname)
    - theme_color {string} - Theme color for the chat widget
-->
<template>
  <div
    :ref="facebook.ref"
    :page_id="facebook.page_id"
    :data-href="facebook['data-href']"
    :messenger_app_id="facebook.messenger_app_id"
    :theme_color="facebook.theme_color"
    class="fb-customerchat"
  />
</template>

<script setup>
/**
 * @module components/Facebook
 * @description Facebook Messenger Customer Chat integration (Vue 3 Composition API)
 */
import { onMounted } from 'vue'

/**
 * Component props
 * @property {Object} facebook - Facebook configuration object
 */
const props = defineProps({
  facebook: {
    type: Object,
    default() {
      return {
        page_id: '',
        messenger_app_id: '',
        'data-href': 'https://keyreply.com',
        ref: location.hostname,
        // theme_color: "#E4392B",
      }
    },
  },
})

/**
 * Initialize Facebook SDK on component mount
 */
onMounted(() => {
  window.fbAsyncInit = () => {
    window.FB.init({
      appId: props.facebook.messenger_app_id,
      xfbml: true,
      version: 'v2.12',
    })
  }

  ;(function (d, s, id) {
    var js,
      fjs = d.getElementsByTagName(s)[0]
    if (d.getElementById(id)) {
      return
    }
    js = d.createElement(s)
    js.id = id
    js.src = 'https://connect.facebook.net/en_US/sdk.js'
    fjs.parentNode.insertBefore(js, fjs)
  })(document, 'script', 'facebook-jssdk')
})

/**
 * Show the Facebook Customer Chat dialog
 */
function show() {
  window.FB.CustomerChat.showDialog()
}

/**
 * Hide the Facebook Customer Chat dialog
 */
function hide() {
  window.FB.CustomerChat.hideDialog()
}

/**
 * Expose show/hide methods for parent component access
 */
defineExpose({
  show,
  hide,
})
</script>
<!--
<style>
#fb-root .fb_dialog {
  visibility: hidden !important;
}

#fb-root iframe[kwframeid="2"] {
  bottom: 10pt !important;
  right: 50pt !important;
}
</style>
-->

<!-- Css with RTL generated by script -->
<style>
#fb-root .fb_dialog {
  visibility: hidden !important;
}
/*@noflip*/
html[dir="ltr"] #fb-root iframe[kwframeid="2"] {
  bottom: 10pt !important;
  right: 50pt !important;
}
/*@noflip*/
html[dir="rtl"] #fb-root iframe[kwframeid="2"] {
  bottom: 10pt !important;
  left: 50pt !important;
}
</style>
